import { useEffect, useState } from "react";
import { Form, Link, useLocation, useParams } from "react-router";
import type { LoaderFunctionArgs } from "react-router";
import { useSuspenseInfiniteQuery } from "@tanstack/react-query";
import type { Member } from "@repo/shared-types";
import { ChevronRight, Search, X } from "lucide-react";
import { mayManage, powersOver, type Viewer } from "@/lib/grants";
import { queries } from "@/lib/queries";
import { readPages } from "@/lib/query";
import { Avatar } from "@/components/avatar";
import { EmptyNote } from "@/components/empty-note";
import { MemberSheet, RoleBadge } from "@/components/member-sheet";
import { ShowMore, rowsOf } from "@/components/show-more";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from "@/components/ui/input-group";
import { Item, ItemActions, ItemContent, ItemDescription, ItemMedia, ItemTitle } from "@/components/ui/item";
import { SectionHeader, loadSection, useSection } from "@/routes/space-section";

/**
 * Who is in a space. Everybody in it reads the list — a picture, a name, a
 * handle and a role each (decided with Tiago 12 October). A member the
 * viewer may manage opens a sheet: the owner changes roles and grants there
 * and hands the space on, and whoever manages members removes one. Nothing
 * else on the list is tappable. Leaving is the space's menu's, where it asks
 * first.
 *
 * The list is paged, 50 a time, with "Show more" under it, and searchable:
 * the term lives in the address (`?q=`) like the spaces page's, and the api
 * matches it against the name anywhere and the start of the username
 * (0084). The sheet works for a member on any page read so far.
 *
 * A personal space has exactly one member for its whole life, so nothing
 * here applies to it. The api and the database refuse it too; not offering
 * it is about not drawing something that would be refused.
 */

/** The search term in the address, as the box shows it. */
function searchOf(search: string): string {
  return new URLSearchParams(search).get("q")?.trim() ?? "";
}

export async function membersLoader({ request, params }: LoaderFunctionArgs) {
  await loadSection(request, params, "members");
  await readPages(queries.members(params.slug ?? "", searchOf(new URL(request.url).search)));
  return null;
}

export default function SpaceMembers() {
  const slug = useParams().slug ?? "";
  const query = searchOf(useLocation().search);
  const { tenant, me, isOwner, canManageMembers } = useSection(slug);
  const pages = useSuspenseInfiniteQuery(queries.members(slug, query));
  const members = rowsOf(pages.data, (page) => page.members, (member) => member.userId);
  const viewer: Viewer = { me, isOwner, canManageMembers, isPersonal: tenant.isPersonal };
  const [open, setOpen] = useState<string | null>(null);

  // The sheet is drawn from the pages as last read, so a member removed, or
  // a space handed on, closes it: nothing is left to do there.
  const selected = members.find((member) => member.userId === open);
  const powers = selected ? powersOver(viewer, selected) : null;
  const showing = !!(selected && powers && mayManage(powers));
  useEffect(() => {
    if (open && !showing) setOpen(null);
  }, [open, showing]);

  return (
    <section className="space-y-6">
      <SectionHeader tenant={tenant} title="Members" />

      <MemberSearch query={query} />

      {members.length === 0 ? (
        <EmptyNote icon={Search}>{query ? `Nobody matches “${query}”.` : "Nobody is in this space."}</EmptyNote>
      ) : (
        <Card className="gap-0 py-1.5">
          <ul className="divide-y divide-border">
            {members.map((member) => (
              <li key={member.userId}>
                {mayManage(powersOver(viewer, member)) ? (
                  // The whole row is the button: shadcn's item drawn on its button.
                  <Item asChild className="rounded-none">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setOpen(member.userId)}
                      className="h-auto justify-start text-left font-normal whitespace-normal focus-visible:ring-inset"
                    >
                      <MemberLine member={member} />
                      <ChevronRight aria-hidden="true" className="text-muted-foreground" />
                    </Button>
                  </Item>
                ) : (
                  <Item className="rounded-none">
                    <MemberLine member={member} />
                  </Item>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}

      <ShowMore query={pages} />

      {showing && selected && powers && (
        <MemberSheet key={selected.userId} tenant={tenant} member={selected} powers={powers} onClose={() => setOpen(null)} />
      )}
    </section>
  );
}

/**
 * Finding somebody: one box, sent with the keyboard's Search key. A GET form,
 * so the term lands in the address and the loader does the work, as the
 * spaces page's does.
 */
function MemberSearch({ query }: { query: string }) {
  const { pathname } = useLocation();
  return (
    <Form method="get" role="search">
      <InputGroup className="h-10">
        <InputGroupAddon>
          <Search aria-hidden="true" />
        </InputGroupAddon>
        {/* Keyed by the term, so clearing it empties the box. */}
        <InputGroupInput
          key={query}
          name="q"
          defaultValue={query}
          placeholder="Search members"
          aria-label="Search members by name or username"
          inputMode="search"
          enterKeyHint="search"
          autoComplete="off"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
        />
        {query && (
          <InputGroupAddon align="inline-end">
            <InputGroupButton asChild size="icon-xs">
              <Link to={pathname} aria-label="Clear the search">
                <X aria-hidden="true" />
              </Link>
            </InputGroupButton>
          </InputGroupAddon>
        )}
      </InputGroup>
    </Form>
  );
}

/** A row's picture, name, handle and role; the name and handle give way on a narrow screen. */
function MemberLine({ member }: { member: Member }) {
  return (
    <>
      <ItemMedia>
        <Avatar person={{ ...member, id: member.userId }} size="md" />
      </ItemMedia>
      <ItemContent className="min-w-0 gap-0.5">
        <ItemTitle className="block w-full truncate">{member.name || member.username}</ItemTitle>
        <ItemDescription className="truncate">@{member.username}</ItemDescription>
      </ItemContent>
      <ItemActions>
        <RoleBadge role={member.role} />
      </ItemActions>
    </>
  );
}
